import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, QrCode, MapPin, ArrowClockwise, Fingerprint, Info, Sparkle, Clock } from "@phosphor-icons/react";
import { SearchBar } from "./_shared";
import { PlanGatedButton } from "@/components/PlanGating";
import { useAddons } from "@/contexts/AddonsContext";
import { devLog } from "@/lib/devLog";
import { timezoneLabel } from "@/lib/helpers";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";


const emptyAttendance = () => ({
  attendance_method: "manual",
  gps_enabled: false,
  gps_lat: null,
  gps_lng: null,
  gps_radius_m: 100,
  qr_auto_regen_secs: 0,
  biometric_enabled: false,
  // iter-55 — Business Hours + Overtime defaults per spec.
  working_days: [1, 2, 3, 4, 5],
  business_start: "09:00",
  business_end: "18:00",
  grace_period_min: 15,
  late_arrival_cutoff_min: 120,
  early_arrival_window_min: 30,
  early_exit_threshold_min: 60,
  half_day_threshold_pct: 50,
  overtime_enabled: false,
  overtime_threshold_hours: 1,
  overtime_streak_days: 5,
  overtime_monthly_min_hours: 10,
});


export default function BranchesTab() {
  const { hasEnterpriseWorkforce } = useAddons();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = () => ({
    name: "", code: "", address: "", phone: "", is_head_office: false,
    timezone: "Asia/Kolkata",
    attendance: emptyAttendance(),
  });
  const [form, setForm] = useState(emptyForm());
  const [regenerating, setRegenerating] = useState(false);
  const [zones, setZones] = useState(["Asia/Kolkata"]);

  const load = async () => {
    try { setItems((await api.get("/branches")).data || []); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/branches/timezones");
        setZones(r.data?.timezones || ["Asia/Kolkata"]);
      } catch (e) { devLog("timezone catalog fetch failed", e); }
    })();
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setShow(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "",
      code: b.code || "",
      address: b.address || "",
      phone: b.phone || "",
      is_head_office: !!b.is_head_office,
      timezone: b.timezone || "Asia/Kolkata",
      attendance: { ...emptyAttendance(), ...(b.attendance || {}) },
    });
    setShow(true);
  };

  const submit = async () => {
    if (!form.name || !form.code) { toast.error("Name and code required"); return; }
    // Extra guardrail — the backend also enforces this but a friendly
    // toast beats a 400.
    if (form.attendance.attendance_method === "qr" && form.attendance.gps_enabled) {
      const { gps_lat, gps_lng } = form.attendance;
      if (gps_lat === null || gps_lat === "" || gps_lng === null || gps_lng === "") {
        toast.error("Enter GPS coordinates or turn off GPS tagging");
        return;
      }
    }
    try {
      const payload = {
        name: form.name, code: form.code,
        address: form.address, phone: form.phone,
        is_head_office: form.is_head_office,
        timezone: form.timezone || "Asia/Kolkata",
        attendance: {
          ...form.attendance,
          gps_lat: form.attendance.gps_lat === "" ? null : (form.attendance.gps_lat === null ? null : Number(form.attendance.gps_lat)),
          gps_lng: form.attendance.gps_lng === "" ? null : (form.attendance.gps_lng === null ? null : Number(form.attendance.gps_lng)),
          gps_radius_m: Number(form.attendance.gps_radius_m) || 100,
          qr_auto_regen_secs: Number(form.attendance.qr_auto_regen_secs) || 0,
        },
      };
      if (editing) {
        await api.put(`/branches/${editing.id}`, payload);
        toast.success("Branch updated");
      } else {
        await api.post("/branches", payload);
        toast.success("Branch added");
      }
      setShow(false); setEditing(null); setForm(emptyForm());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const regenerateQr = async () => {
    if (!editing) return;
    if (!window.confirm("Regenerate the QR code? All previously-printed codes for this branch will stop working immediately.")) return;
    setRegenerating(true);
    try {
      await api.post(`/branches/${editing.id}/regenerate-qr`);
      toast.success("QR code regenerated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRegenerating(false); }
  };

  const toggleActive = async (b) => {
    const nextActive = b.active === false;
    try {
      await api.put(`/branches/${b.id}`, { active: nextActive });
      toast.success(nextActive ? "Branch activated" : "Branch deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = q.trim()
    ? items.filter(b => {
        const s = q.trim().toLowerCase();
        return (b.name || "").toLowerCase().includes(s)
          || (b.code || "").toLowerCase().includes(s)
          || (b.address || "").toLowerCase().includes(s)
          || (b.phone || "").toLowerCase().includes(s);
      })
    : items;

  const attn = form.attendance;
  const isQr = attn.attendance_method === "qr";

  return (
    <Card>
      <CardHeader className="gap-3">
        <div className="flex flex-row justify-between items-center gap-3">
          <CardTitle className="text-base font-display tracking-tight">Branches ({filtered.length}{q && ` / ${items.length}`})</CardTitle>
          <PlanGatedButton flagKey="can_add_branch" size="sm" onClick={openNew} className="gap-1.5" data-testid="new-branch-btn"><Plus size={14} weight="bold" />New</PlanGatedButton>
        </div>
        <SearchBar value={q} onChange={setQ} placeholder="Search by name, code, address or phone…" testId="branches-search" />
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-border">
          {filtered.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              {q ? `No branches match "${q}"` : "No branches yet."}
            </div>
          )}
          {filtered.map(b => {
            const inactive = b.active === false;
            const a = b.attendance || {};
            return (
              <div key={b.id} className={`px-5 py-3 flex items-center gap-3 ${inactive ? "opacity-60" : ""}`} data-testid={`branch-row-${b.id}`}>
                <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center font-mono text-xs font-bold">{b.code}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    {b.name}
                    {b.is_head_office && <Badge variant="outline" className="text-[10px]">HQ</Badge>}
                    {inactive && <Badge className="text-[10px] bg-muted text-muted-foreground border-border">Inactive</Badge>}
                    {a.attendance_method === "qr" ? (
                      <Badge className="text-[10px] bg-primary/10 text-primary border-primary/30 gap-0.5"><QrCode size={10} weight="fill" /> QR</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Manual attendance</Badge>
                    )}
                    {a.gps_enabled && <Badge className="text-[10px] bg-sky-100 text-sky-800 border-sky-200 gap-0.5"><MapPin size={10} weight="fill" /> GPS</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{b.address || "—"} {b.phone && `· ${b.phone}`} · <span className="font-mono">{b.timezone || "Asia/Kolkata"}</span></div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`} className="gap-1.5"><PencilSimple size={14} /> Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => toggleActive(b)} data-testid={`toggle-branch-${b.id}`} className="text-xs">
                  {inactive ? "Activate" : "Deactivate"}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={show} onOpenChange={(o) => { setShow(o); if (!o) { setEditing(null); } }}>
        <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit branch" : "New branch"}</DialogTitle>
            <DialogDescription className="text-xs">
              Configure branch identity plus its staff-attendance method (Manual by default; QR + GPS unlock with the Enterprise Workforce Suite add-on).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="branch-name" /></div>
                <div className="space-y-1.5"><Label>Code</Label><Input value={form.code} onChange={e=>setForm({...form, code:e.target.value.toUpperCase()})} placeholder="e.g. KOR" data-testid="branch-code" /></div>
              </div>
              <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={e=>setForm({...form, address:e.target.value})} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} /></div>
              <label className="flex items-center gap-2"><Checkbox checked={form.is_head_office} onCheckedChange={v=>setForm({...form, is_head_office:!!v})} /><span className="text-sm">Head office</span></label>

              {/* ============ Time zone ============ */}
              <div className="space-y-1.5 rounded-lg border border-border bg-muted/30 p-3">
                <Label className="flex items-center gap-1.5">
                  <Clock size={14} weight="duotone" className="text-primary" />
                  Time zone
                </Label>
                <Select
                  value={form.timezone}
                  onValueChange={v => setForm({ ...form, timezone: v })}
                >
                  <SelectTrigger data-testid="branch-timezone"><SelectValue placeholder="Select a time zone" /></SelectTrigger>
                  <SelectContent className="bg-card max-h-72">
                    <SelectItem value="Asia/Kolkata">
                      Asia/Kolkata — India Standard Time (IST, UTC+05:30)
                    </SelectItem>
                    {zones.filter(z => z !== "Asia/Kolkata").map(z => (
                      <SelectItem key={z} value={z}>{z}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-muted-foreground leading-relaxed">
                  Every date and time in this workspace — attendance check-in and
                  check-out, session start and end, reports, exports, login activity
                  and audit trails — is recorded in UTC and displayed on this clock.
                  The head-office branch's zone applies workspace-wide.
                </div>
              </div>
            </div>

            {/* ============ Attendance Settings ============ */}
            <div className="border-t border-border pt-4">
              <div className="mb-3 flex items-center gap-2">
                <QrCode size={16} weight="duotone" className="text-primary" />
                <div className="font-medium text-sm">Staff attendance</div>
                {!hasEnterpriseWorkforce && (
                  <Badge className="text-[10px] bg-amber-100 text-amber-900 border-amber-200 gap-0.5">
                    <Info size={10} /> Add-on required for QR/GPS
                  </Badge>
                )}
              </div>

              {/* Attendance method */}
              <div
                className="rounded-md border border-border bg-muted/30 p-3 flex items-center justify-between gap-3"
                data-testid="branch-attendance-method-row"
              >
                <div>
                  <div className="text-sm font-medium">Attendance method</div>
                  <div className="text-[11px] text-muted-foreground">
                    {isQr
                      ? "QR-based check-in — staff scan a branch-unique QR code."
                      : "Manual — HR/admin marks Present/Absent/Leave from the Attendance page."}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={!isQr ? "text-sm font-medium" : "text-sm text-muted-foreground"}>Manual</span>
                  <Switch
                    checked={isQr}
                    disabled={!hasEnterpriseWorkforce}
                    onCheckedChange={(v) => setForm(f => ({
                      ...f,
                      attendance: { ...f.attendance, attendance_method: v ? "qr" : "manual" },
                    }))}
                    data-testid="branch-attendance-method"
                  />
                  <span className={isQr ? "text-sm font-medium" : "text-sm text-muted-foreground"}>QR</span>
                </div>
              </div>

              {/* GPS + QR autoregen — visible only when QR is active */}
              {isQr && (
                <div className="mt-3 space-y-3">
                  <div
                    className="rounded-md border border-border p-3 flex items-center justify-between gap-3"
                    data-testid="branch-gps-row"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin size={14} weight="duotone" className="text-sky-600" />
                      <div>
                        <div className="text-sm font-medium">GPS Tagging</div>
                        <div className="text-[11px] text-muted-foreground">
                          Capture device GPS at scan time; flag check-ins outside the geofence.
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={!!attn.gps_enabled}
                      disabled={!hasEnterpriseWorkforce}
                      onCheckedChange={(v) => setForm(f => ({ ...f, attendance: { ...f.attendance, gps_enabled: !!v } }))}
                      data-testid="branch-gps-enabled"
                    />
                  </div>

                  {attn.gps_enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Latitude</Label>
                        <Input
                          type="number" step="any"
                          value={attn.gps_lat ?? ""}
                          onChange={e => setForm(f => ({ ...f, attendance: { ...f.attendance, gps_lat: e.target.value } }))}
                          placeholder="12.9716"
                          data-testid="branch-gps-lat"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Longitude</Label>
                        <Input
                          type="number" step="any"
                          value={attn.gps_lng ?? ""}
                          onChange={e => setForm(f => ({ ...f, attendance: { ...f.attendance, gps_lng: e.target.value } }))}
                          placeholder="77.5946"
                          data-testid="branch-gps-lng"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Geofence radius (m)</Label>
                        <Input
                          type="number" min={5} max={5000}
                          value={attn.gps_radius_m}
                          onChange={e => setForm(f => ({ ...f, attendance: { ...f.attendance, gps_radius_m: e.target.value } }))}
                          data-testid="branch-gps-radius"
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Auto-regenerate QR every (seconds)</Label>
                      <Input
                        type="number" min={0} max={86400}
                        value={attn.qr_auto_regen_secs}
                        onChange={e => setForm(f => ({ ...f, attendance: { ...f.attendance, qr_auto_regen_secs: e.target.value } }))}
                        placeholder="0 = never"
                        data-testid="branch-qr-autoregen"
                      />
                      <p className="text-[10px] text-muted-foreground">Zero = never auto-rotates. Suggested for high-security sites: 30-120s.</p>
                    </div>
                    {editing && attn.attendance_method === "qr" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Manual actions</Label>
                        <Button
                          type="button" variant="outline"
                          className="gap-1.5 w-full"
                          onClick={regenerateQr}
                          disabled={regenerating}
                          data-testid="branch-qr-regenerate"
                        >
                          <ArrowClockwise size={13} /> {regenerating ? "Rotating…" : "Regenerate QR now"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Biometric — future ready. Hidden control is stored, no UI toggle wired to a real device yet. */}
              <div
                className="mt-3 rounded-md border border-dashed border-border p-3 flex items-center justify-between gap-3"
                data-testid="branch-biometric-row"
              >
                <div className="flex items-center gap-2">
                  <Fingerprint size={14} weight="duotone" className="text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      Biometric check-in
                      <Badge className="text-[9px] bg-slate-100 text-slate-700 border-slate-200 gap-0.5"><Sparkle size={9} weight="fill" /> Coming soon</Badge>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Reserved toggle so a fingerprint / face device can be enabled later without a schema change.
                    </div>
                  </div>
                </div>
                <Switch
                  checked={!!attn.biometric_enabled}
                  disabled={!hasEnterpriseWorkforce}
                  onCheckedChange={(v) => setForm(f => ({ ...f, attendance: { ...f.attendance, biometric_enabled: !!v } }))}
                  data-testid="branch-biometric-enabled"
                />
              </div>

              {/* iter-55 — Business Hours + Overtime settings */}
              <BusinessHoursPanel
                attn={attn}
                editingId={editing?.id}
                onChange={(patch) => setForm(f => ({ ...f, attendance: { ...f.attendance, ...patch } }))}
                enterpriseActive={hasEnterpriseWorkforce}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="save-branch">{editing ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


/* ============ iter-55 — Business Hours & Overtime panel ============ */

const DAY_LABELS = [
  { iso: 1, short: "Mon" }, { iso: 2, short: "Tue" }, { iso: 3, short: "Wed" },
  { iso: 4, short: "Thu" }, { iso: 5, short: "Fri" }, { iso: 6, short: "Sat" },
  { iso: 7, short: "Sun" },
];

function BusinessHoursPanel({ attn, editingId, onChange, enterpriseActive }) {
  const [applyingAll, setApplyingAll] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const days = Array.isArray(attn.working_days) ? attn.working_days : [1, 2, 3, 4, 5];
  const dayHours = attn.day_hours || {};

  const toggleDay = (iso) => {
    const next = days.includes(iso) ? days.filter(d => d !== iso) : [...days, iso].sort();
    onChange({ working_days: next });
  };

  // iter-56 — per-day start/end helper. Reads with fallback to the
  // top-level business_start/business_end so branches that haven't
  // customised any day still work exactly like before.
  const getDayStart = (iso) => (dayHours[String(iso)] || {}).start || attn.business_start || "09:00";
  const getDayEnd   = (iso) => (dayHours[String(iso)] || {}).end   || attn.business_end   || "18:00";
  const setDayTime = (iso, key, value) => {
    const nextDayHours = { ...dayHours };
    const row = { ...(nextDayHours[String(iso)] || {}) };
    // Ensure the row always carries both endpoints so the override is
    // fully self-contained and doesn't silently re-read the default.
    row.start = row.start || getDayStart(iso);
    row.end   = row.end   || getDayEnd(iso);
    row[key] = value;
    nextDayHours[String(iso)] = row;
    onChange({ day_hours: nextDayHours });
  };
  const copyFirstDayToAll = () => {
    if (!days.length) return;
    const src = days[0];
    const start = getDayStart(src);
    const end   = getDayEnd(src);
    const next = { ...dayHours };
    days.forEach((iso) => { next[String(iso)] = { start, end }; });
    // Also update the top-level defaults so newly-toggled-on days
    // inherit the same. Not strictly necessary but matches admin intent.
    onChange({ day_hours: next, business_start: start, business_end: end });
    toast.success("Copied first day's timing to all working days");
  };

  const applyToAll = async () => {
    if (!editingId) {
      toast.error("Save this branch first, then Apply to All.");
      return;
    }
    setApplyingAll(true);
    try {
      const { data } = await api.post(`/branches/${editingId}/apply-business-hours`);
      toast.success(`Applied to ${data.updated} branch(es). Business Hours copied.`);
      setConfirmOpen(false);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to apply to all branches"));
    } finally {
      setApplyingAll(false);
    }
  };

  return (
    <div className="mt-4 rounded-md border border-border p-3.5 space-y-3.5" data-testid="branch-business-hours-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">Business Hours</div>
          <div className="text-[11px] text-muted-foreground">
            Working days, business timing, and attendance thresholds for this branch.
          </div>
        </div>
        <Button
          size="sm" variant="outline"
          onClick={() => setConfirmOpen(true)}
          disabled={!editingId || applyingAll}
          className="h-7 text-xs"
          data-testid="apply-hours-all-btn"
        >
          Apply to all branches
        </Button>
      </div>

      {/* Working days */}
      <div>
        <Label className="text-xs">Working days</Label>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {DAY_LABELS.map(d => {
            const on = days.includes(d.iso);
            return (
              <button
                key={d.iso} type="button"
                onClick={() => toggleDay(d.iso)}
                data-testid={`working-day-${d.iso}`}
                className={"px-3 py-1 rounded-md text-xs font-medium transition-colors border " +
                  (on ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-transparent hover:bg-muted/70")}
              >{d.short}</button>
            );
          })}
        </div>
      </div>

      {/* iter-56 — Per-day business hours */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div>
            <Label className="text-xs">Business hours per day</Label>
            <div className="text-[10px] text-muted-foreground">
              Some days can run reduced hours (e.g. a half-day Saturday). Edit each row independently.
            </div>
          </div>
          {days.length > 1 && (
            <Button size="sm" variant="ghost" className="h-7 text-[11px]"
              onClick={copyFirstDayToAll}
              data-testid="copy-first-day-timing"
            >Copy first day to all</Button>
          )}
        </div>
        <div className="rounded-md border border-border overflow-hidden">
          <div className="grid grid-cols-[80px_1fr_1fr_auto] gap-2 px-2.5 py-1.5 bg-muted/40 text-[10px] font-medium uppercase text-muted-foreground">
            <div>Day</div><div>Start</div><div>End</div><div className="text-right">Hours</div>
          </div>
          {days.length === 0 && (
            <div className="p-3 text-xs text-muted-foreground italic">
              No working days selected. Toggle the chips above to add days.
            </div>
          )}
          {days.map((iso) => {
            const label = DAY_LABELS.find(d => d.iso === iso)?.short || `Day ${iso}`;
            const s = getDayStart(iso);
            const e = getDayEnd(iso);
            let hours = "—";
            try {
              const [sh, sm] = s.split(":").map(Number);
              const [eh, em] = e.split(":").map(Number);
              const mins = (eh * 60 + em) - (sh * 60 + sm);
              hours = mins > 0 ? `${(mins / 60).toFixed(1)} h` : "0";
            } catch { /* keep placeholder */ }
            return (
              <div key={iso} className="grid grid-cols-[80px_1fr_1fr_auto] gap-2 px-2.5 py-1.5 items-center border-t border-border/60">
                <div className="text-xs font-medium">{label}</div>
                <Input
                  type="time" value={s}
                  onChange={(ev) => setDayTime(iso, "start", ev.target.value)}
                  className="h-8 text-xs"
                  data-testid={`day-${iso}-start`}
                />
                <Input
                  type="time" value={e}
                  onChange={(ev) => setDayTime(iso, "end", ev.target.value)}
                  className="h-8 text-xs"
                  data-testid={`day-${iso}-end`}
                />
                <div className="text-xs text-right text-muted-foreground w-16">{hours}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timing thresholds — apply the same to every working day */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        <div>
          <Label className="text-[11px]">Grace period (min)</Label>
          <Input type="number" min={0} max={120} value={attn.grace_period_min ?? 15}
            onChange={e => onChange({ grace_period_min: Number(e.target.value) })}
            data-testid="grace-period" />
        </div>
        <div>
          <Label className="text-[11px]">Late arrival cutoff (min)</Label>
          <Input type="number" min={15} max={600} value={attn.late_arrival_cutoff_min ?? 120}
            onChange={e => onChange({ late_arrival_cutoff_min: Number(e.target.value) })}
            data-testid="late-arrival-cutoff" />
        </div>
        <div>
          <Label className="text-[11px]">Early arrival window (min)</Label>
          <Input type="number" min={0} max={240} value={attn.early_arrival_window_min ?? 30}
            onChange={e => onChange({ early_arrival_window_min: Number(e.target.value) })}
            data-testid="early-arrival-window" />
        </div>
        <div>
          <Label className="text-[11px]">Early exit threshold (min)</Label>
          <Input type="number" min={0} max={240} value={attn.early_exit_threshold_min ?? 60}
            onChange={e => onChange({ early_exit_threshold_min: Number(e.target.value) })}
            data-testid="early-exit-threshold" />
        </div>
        <div>
          <Label className="text-[11px]">Half-day threshold (%)</Label>
          <Input type="number" min={0} max={100} value={attn.half_day_threshold_pct ?? 50}
            onChange={e => onChange({ half_day_threshold_pct: Number(e.target.value) })}
            data-testid="half-day-threshold" />
        </div>
      </div>

      {/* Save-time notice */}
      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 flex items-start gap-1.5">
        <Info size={12} weight="duotone" className="mt-0.5 shrink-0" />
        <span>
          Existing attendance records are not recalculated. Only new records created after saving will use the updated settings.
        </span>
      </div>

      {/* Enterprise-only: Overtime */}
      {enterpriseActive && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 space-y-3" data-testid="overtime-panel">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium flex items-center gap-1.5">
                Overtime tracking
                <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border-emerald-200">Enterprise</Badge>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Log overtime beyond business hours + trigger Overtime Achiever recognition.
              </div>
            </div>
            <Switch
              checked={!!attn.overtime_enabled}
              onCheckedChange={v => onChange({ overtime_enabled: !!v })}
              data-testid="overtime-enabled"
            />
          </div>
          {attn.overtime_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
              <div>
                <Label className="text-[11px]">Overtime threshold (hours)</Label>
                <Input type="number" min={0} max={12} step={0.5} value={attn.overtime_threshold_hours ?? 1}
                  onChange={e => onChange({ overtime_threshold_hours: Number(e.target.value) })}
                  data-testid="overtime-threshold" />
                <div className="text-[10px] text-muted-foreground">Extra hours beyond business hours needed before OT starts logging.</div>
              </div>
              <div>
                <Label className="text-[11px]">Streak days for recognition</Label>
                <Input type="number" min={1} max={31} value={attn.overtime_streak_days ?? 5}
                  onChange={e => onChange({ overtime_streak_days: Number(e.target.value) })}
                  data-testid="overtime-streak" />
                <div className="text-[10px] text-muted-foreground">Consecutive working days with overtime to trigger the badge.</div>
              </div>
              <div>
                <Label className="text-[11px]">Monthly minimum OT hours</Label>
                <Input type="number" min={0} max={200} step={0.5} value={attn.overtime_monthly_min_hours ?? 10}
                  onChange={e => onChange({ overtime_monthly_min_hours: Number(e.target.value) })}
                  data-testid="overtime-monthly-min" />
                <div className="text-[10px] text-muted-foreground">Total monthly OT hours above which recognition triggers.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Apply-to-all confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle>Apply to all branches?</DialogTitle>
            <DialogDescription>
              This copies the current branch&apos;s Business Hours (and Overtime settings when
              enabled) to <b>every other active branch</b>, overwriting whatever they had.
              QR / GPS / biometric settings are not touched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={applyToAll} disabled={applyingAll} data-testid="apply-hours-confirm">
              {applyingAll ? "Applying…" : "Apply to all branches"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
